/**
 * The chat-side entry point into the HTML reviewer.
 *
 * When the model writes an `.html` file, the interesting thing is not the tool
 * call — it is the rendered artifact. This replaces the generic "Writing
 * screens/home.html" row with a card that opens the review surface, and shows
 * how many comments already sit on it.
 */
import { useCallback, useEffect, useState } from 'react';
import { ExternalLink, FileCode2, MessageSquare } from 'lucide-react';
import { api } from '@/services/api';
import { feedbackPathFor, toWorkspacePath } from '../review/feedback-store';
import { Shimmer, DELIVERABLE_SURFACE } from '../toolRow';

/**
 * Pulls the `.html` targets out of a write tool's arguments, if any.
 *
 * Targets come back as bare paths: the model addresses files as `ws://…`, and
 * that scheme is meaningless to every route the reviewer then calls.
 */
export function extractHtmlTargets(toolName: string, args?: Record<string, unknown>): string[] {
  const isHtml = (p: unknown): p is string => typeof p === 'string' && /\.html?$/i.test(p);

  if (toolName === 'fs_write') {
    return isHtml(args?.target_file) ? [toWorkspacePath(args.target_file as string)] : [];
  }
  if (toolName === 'fs_batch_write') {
    const files = args?.files;
    if (typeof files !== 'object' || files === null) return [];
    return Object.keys(files as Record<string, unknown>).filter(isHtml).map(toWorkspacePath);
  }
  return [];
}

export function reviewUrlFor(threadId: string, path: string): string {
  return `/review/${threadId}?path=${encodeURIComponent(path)}`;
}

export function openReviewer(threadId: string, path: string): void {
  window.open(reviewUrlFor(threadId, path), '_blank', 'noopener');
}

interface HtmlArtifactCardProps {
  threadId: string | null;
  paths: string[];
  /** True while the write is still in flight. */
  pending?: boolean;
}

export function HtmlArtifactCard({ threadId, paths, pending }: HtmlArtifactCardProps) {
  return (
    <div className={`${DELIVERABLE_SURFACE} divide-y divide-zinc-100 overflow-hidden`}>
      {paths.map((path) => (
        <ArtifactRow key={path} threadId={threadId} path={path} disabled={!!pending} pending={!!pending} />
      ))}
    </div>
  );
}

function ArtifactRow({
  threadId,
  path,
  disabled,
  pending,
}: {
  threadId: string | null;
  path: string;
  disabled: boolean;
  pending: boolean;
}) {
  const count = useFeedbackCount(threadId, path, disabled);
  const canOpen = !!threadId && !disabled;
  const fileName = path.split('/').pop() || path;

  const open = useCallback(() => {
    if (canOpen && threadId) openReviewer(threadId, path);
  }, [canOpen, threadId, path]);

  return (
    <div
      onDoubleClick={open}
      className={`group flex items-center gap-2.5 px-3 py-2 transition-colors ${canOpen ? 'cursor-pointer hover:bg-zinc-50' : ''}`}
      title={canOpen ? `Double-click to review ${fileName}` : undefined}
    >
      <FileCode2 className="w-3.5 h-3.5 shrink-0 text-zinc-400" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-meta text-zinc-900">
          {pending ? <Shimmer>Writing {fileName}</Shimmer> : fileName}
        </div>
        <div className="truncate font-mono text-xs text-zinc-400">{path}</div>
      </div>

      {count > 0 && (
        <span
          className="inline-flex shrink-0 items-center gap-1 text-xs text-zinc-500 tabular-nums"
          title={`${count} comment${count === 1 ? '' : 's'} in ${feedbackPathFor(path)}`}
        >
          <MessageSquare className="w-3 h-3" />
          {count}
        </span>
      )}

      {canOpen && (
        <button
          type="button"
          onClick={open}
          className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md border border-zinc-200 bg-white px-2 text-xs text-zinc-700 transition-colors hover:border-zinc-300 hover:bg-zinc-50 hover:text-zinc-900"
        >
          Review
          <ExternalLink className="w-3 h-3 text-zinc-400" />
        </button>
      )}
    </div>
  );
}

/**
 * Comment count for an artifact, read from its sibling feedback file.
 *
 * Refetched when the window regains focus, since the reviewer opens in another
 * tab and the count is stale the moment the user comments over there.
 */
function useFeedbackCount(threadId: string | null, path: string, disabled: boolean): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!threadId || disabled) return;
    let cancelled = false;

    const refresh = () => {
      api
        .get<{ type: string; content?: string }>(`/chat/threads/${threadId}/workspace/preview`, {
          path: feedbackPathFor(path),
        })
        .then((data) => {
          if (cancelled || data?.type !== 'text' || !data.content) return;
          const parsed = JSON.parse(data.content) as { feedbacks?: unknown[] };
          if (Array.isArray(parsed?.feedbacks)) setCount(parsed.feedbacks.length);
        })
        .catch(() => {
          // No feedback file yet — the common case.
        });
    };

    refresh();
    window.addEventListener('focus', refresh);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', refresh);
    };
  }, [threadId, path, disabled]);

  return count;
}
